// The wire shape of an API error, in one place.
//
// A route handler throws a `LogbookApiError`; app-base's dispatcher runs it through
// `AppBase.Http.Server.errorResponse`, which serialises the `ApiError` conformance in
// `Sources/LogbookApi/Api.swift` as `{error: "<case name>", status: <code>}`. `Client.call`
// catches the resulting `HttpStatusError` and re-throws that body with an added
// `httpStatus` (see App/Client.ts).
//
// Before the move to app-base routing the server was Vapor, whose `ErrorMiddleware`
// wrote `{error: true, reason: "<case name>"}` instead. Call sites that still tested for
// that shape silently stopped recognising every error -- the cause of #1449 and #1820 --
// so the tolerance for it lives here and nowhere else: a caller asks for the reason, not
// for a shape.

interface CurrentApiErrorBody {
  error?: unknown;
  status?: unknown;
  httpStatus?: unknown;
  reason?: unknown;
}

function errorBody(error: unknown): CurrentApiErrorBody | undefined {
  return error != null && typeof error == "object" ? error as CurrentApiErrorBody : undefined;
}

/// The server-side error case name (e.g. `"signupEmailAlreadyExists"`), or `undefined`
/// when the rejection carries none -- a network failure, a non-JSON error body, or a
/// plain `Error`. Callers should fall back to a generic message in that case rather than
/// showing nothing.
export function apiErrorReason(error: unknown): string | undefined {
  const body = errorBody(error);
  if (!body) {
    return undefined;
  }
  if (typeof body.error == "string" && body.error.length > 0) {
    return body.error;
  }
  if (body.error === true && typeof body.reason == "string" && body.reason.length > 0) {
    return body.reason;
  }
  return undefined;
}

/// The HTTP status of a rejected API call. `httpStatus` is what the transport records;
/// `status` is the server's own copy in the body, used when the rejection was reconstructed
/// without the transport (e.g. a test fixture).
export function apiErrorStatus(error: unknown): number | undefined {
  const body = errorBody(error);
  if (!body) {
    return undefined;
  }
  if (typeof body.httpStatus == "number") {
    return body.httpStatus;
  }
  return typeof body.status == "number" ? body.status : undefined;
}

/// Whether a rejected API call reported the given error case.
export function isApiError(error: unknown, reason: string): boolean {
  return apiErrorReason(error) === reason;
}
